import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import ApexCharts from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import moment from 'moment';
import { client } from '../../../../../App';
import { GET_AUTOMATED_BAR_CHART } from '../../../../../queries/widgets';
import {
  buildAlignedAutomatedBarChartSeries,
  buildRollingPeriodLabels,
} from '../../../../../utils/formatBarChartPeriodLabel';
import {
  normalizeRollingPerformance,
  SQUAD_ROLLING_BAR_COLORS,
  type RollingPerformanceConfig,
} from './rollingPerformance';
import {
  SQUAD_ROLLING_CHART_GRID_PADDING,
  SQUAD_ROLLING_CHART_Y_LABEL_OFFSET_X,
} from './squadPortfolioChartUtils';
import { SQUAD_COLORS, SQUAD_DEFAULT_ISIN } from './templateConstants';

export const SQUAD_ROLLING_CHART_ID = 'squad-rolling-bar';

function formatDateToUntil(dateStr: string | undefined): string | undefined {
  if (!dateStr) return undefined;
  const raw = String(dateStr).trim();
  const isoPart = raw.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
  if (isoPart && moment(isoPart, 'YYYY-MM-DD', true).isValid()) return isoPart;
  const m = moment(raw);
  return m.isValid() ? m.format('YYYY-MM-DD') : undefined;
}

type Props = {
  data?: Partial<RollingPerformanceConfig> | null;
  historyIsin?: string;
  publishedAt?: string;
  height?: number;
  /** FS-Header primary (brutto) / secondary (netto). */
  primaryColor?: string;
  secondaryColor?: string;
  onHasDataChange?: (hasData: boolean) => void;
};

const formatPctLabel = (val: number) =>
  `${Number(val).toFixed(2).replace('.', ',')}%`;

export const SquadRollingPerformanceChart: FC<Props> = ({
  data,
  historyIsin,
  publishedAt,
  height = 170,
  primaryColor,
  secondaryColor,
  onHasDataChange,
}) => {
  const fallbackIsin = historyIsin || SQUAD_DEFAULT_ISIN;
  const rolling = useMemo(
    () => normalizeRollingPerformance(data, fallbackIsin),
    [data, fallbackIsin],
  );

  const rollingChartType = rolling.chartType;
  const rollingIsin = rolling.isin || fallbackIsin;
  const rollingAutomationType = rolling.automationType;
  const rollingBarColorSets = useMemo(() => {
    const primary = String(primaryColor || '').trim();
    const secondary = String(secondaryColor || '').trim();
    if (primary && secondary) return [primary, secondary];
    if (primary) return [primary, SQUAD_ROLLING_BAR_COLORS[1]];
    if (secondary) return [SQUAD_ROLLING_BAR_COLORS[0], secondary];
    return SQUAD_ROLLING_BAR_COLORS;
  }, [primaryColor, secondaryColor]);

  const [automatedPeriodsData, setAutomatedPeriodsData] = useState<
    Array<{ name: string; data: number[]; color?: string }>
  >([{ name: '', data: [] }]);
  const [automatedPeriodLabels, setAutomatedPeriodLabels] = useState<string[]>([]);
  const [barChartLoading, setBarChartLoading] = useState(false);

  const untilForApi = useMemo(
    () => formatDateToUntil(publishedAt),
    [publishedAt],
  );

  const fetchKey = useMemo(
    () => [rollingChartType, rollingIsin, rollingAutomationType, untilForApi || ''].join('|'),
    [rollingChartType, rollingIsin, rollingAutomationType, untilForApi],
  );

  useEffect(() => {
    if (!rollingIsin || rollingChartType !== 'Automated') return;

    let cancelled = false;

    const load = async () => {
      setBarChartLoading(true);
      try {
        const result = await client.query({
          query: GET_AUTOMATED_BAR_CHART,
          variables: {
            isin: rollingIsin,
            type: rollingAutomationType,
            ...(untilForApi ? { until: untilForApi } : {}),
            ...(untilForApi ? { useFactsheetUntilForCurrentYear: true } : {}),
          },
          fetchPolicy: 'network-only',
        });

        if (cancelled) return;

        const apiData = result.data?.getAutomatedBarChart;
        if (!Array.isArray(apiData) || apiData.length === 0) {
          setAutomatedPeriodLabels([]);
          setAutomatedPeriodsData([{ name: '', data: [] }]);
          return;
        }

        if (rollingAutomationType === 'all') {
          const { labels, series } = buildAlignedAutomatedBarChartSeries(apiData, {
            legend: rolling.legend,
            colors: rollingBarColorSets,
            fallbackColor: SQUAD_COLORS.navy,
          });
          setAutomatedPeriodLabels(labels);
          setAutomatedPeriodsData(
            series.map((item) => ({
              name: item.name,
              data: item.data.map((value) => (value == null ? 0 : value)),
              color: item.color,
            })),
          );
        } else {
          const labelRows = [...apiData].sort(
            (a: any, b: any) =>
              moment(a.end, 'DD.MM.YYYY', true).valueOf() -
              moment(b.end, 'DD.MM.YYYY', true).valueOf(),
          );
          setAutomatedPeriodLabels(buildRollingPeriodLabels(labelRows));
          setAutomatedPeriodsData([
            { name: '', data: labelRows.map((item: any) => item.value) },
          ]);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch SQUAD rolling bar chart:', error);
          setAutomatedPeriodLabels([]);
          setAutomatedPeriodsData([{ name: '', data: [] }]);
        }
      } finally {
        if (!cancelled) setBarChartLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    fetchKey,
    rolling.legend,
    rollingAutomationType,
    rollingBarColorSets,
    rollingChartType,
    rollingIsin,
    untilForApi,
  ]);

  const isBarAutomated = rollingChartType === 'Automated';

  const barChartCategories = useMemo(() => {
    if (isBarAutomated) return automatedPeriodLabels;
    return (rolling.periods || []).map((p) => p.label);
  }, [isBarAutomated, automatedPeriodLabels, rolling.periods]);

  const barChartSeries = useMemo(() => {
    if (isBarAutomated) {
      if (automatedPeriodsData.length > 0 && automatedPeriodsData[0]?.data?.length > 0) {
        return automatedPeriodsData.map((series, index) => ({
          name: series.name || '',
          data: series.data,
          color: rollingBarColorSets[index] || series.color,
        }));
      }
      return [];
    }

    const periods = rolling.periods || [];
    if (!periods.length) return [];

    const hasDual = periods.some((p) => p.brutto != null || p.netto != null);
    if (hasDual) {
      return [
        {
          name: rolling.legend?.gross_perf_year_as_of_today || 'brutto',
          data: periods.map((p) => Number(p.brutto ?? p.value ?? 0)),
          color: rollingBarColorSets[0],
        },
        {
          name: rolling.legend?.perf_year_as_of_today || 'netto (mit 5% Ausgabeaufschlag)',
          data: periods.map((p) => Number(p.netto ?? p.value ?? 0)),
          color: rollingBarColorSets[1] || rollingBarColorSets[0],
        },
      ];
    }

    return [
      {
        name: '',
        data: periods.map((p) => Number(p.value ?? p.brutto ?? 0)),
        color: rollingBarColorSets[0],
      },
    ];
  }, [isBarAutomated, automatedPeriodsData, rolling.periods, rolling.legend, rollingBarColorSets]);

  const hasBarChartData =
    barChartSeries.length > 0 && barChartSeries.some((s) => s.data?.length > 0);

  useEffect(() => {
    onHasDataChange?.(hasBarChartData);
  }, [hasBarChartData, onHasDataChange]);

  const showLegend =
    isBarAutomated ? rollingAutomationType === 'all' : barChartSeries.length > 1;

  const barChartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        id: SQUAD_ROLLING_CHART_ID,
        type: 'bar',
        height,
        toolbar: { show: false },
        animations: { enabled: false },
        // No chart title here, so the 15 px ApexCharts reserves above one is dead space.
        parentHeightOffset: 0,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '85%',
          dataLabels: { position: 'center' },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number) => formatPctLabel(val),
        style: {
          fontSize: '9px',
          fontWeight: 600,
          colors: ['#FFFFFF'],
        },
        dropShadow: { enabled: false },
      },
      stroke: { show: true, width: 0, colors: ['transparent'] },
      xaxis: {
        categories: barChartCategories,
        labels: {
          rotate: 0,
          rotateAlways: false,
          trim: false,
          hideOverlappingLabels: false,
          minHeight: 28,
          maxHeight: 40,
          offsetY: 0,
          // Single-line labels (avoid formatBarChartPeriodLabel's 3-line split)
          formatter: (val: string) => {
            const trimmed = String(val ?? '').trim();
            if (!trimmed) return '';
            const parts = trimmed.split(/\s+bis\s+/i).map((p) => p.trim());
            if (parts.length === 2) {
              const short = (d: string) => {
                const m = moment(d, ['DD.MM.YYYY', 'DD.MM.YY'], true);
                return m.isValid() ? m.format('DD.MM.YY') : d;
              };
              return `${short(parts[0])} bis ${short(parts[1])}`;
            }
            return trimmed;
          },
          style: { fontSize: '8px', colors: [SQUAD_COLORS.navy] },
        },
      },
      yaxis: {
        labels: {
          formatter: (value: number) => `${value}%`,
          offsetX: SQUAD_ROLLING_CHART_Y_LABEL_OFFSET_X,
          style: { fontSize: '9px', colors: [SQUAD_COLORS.navy] },
        },
      },
      grid: {
        borderColor: '#E5E5E5',
        strokeDashArray: 0,
        padding: { ...SQUAD_ROLLING_CHART_GRID_PADDING },
      },
      legend: {
        show: showLegend,
        position: 'bottom',
        fontSize: '10px',
        markers: { size: 5, shape: 'square' },
      },
      colors: rollingBarColorSets,
      fill: { opacity: 1 },
      tooltip: {
        y: { formatter: (val: number) => formatPctLabel(val) },
      },
    }),
    [barChartCategories, height, rollingBarColorSets, showLegend],
  );

  if (hasBarChartData) {
    return (
      <Box sx={{ width: '100%', minHeight: height }}>
        <ReactApexChart
          key={rollingBarColorSets.join('-')}
          type="bar"
          height={height}
          options={barChartOptions}
          series={barChartSeries}
        />
      </Box>
    );
  }

  if (isBarAutomated && barChartLoading) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          fontSize: 12,
        }}
      >
        Chart wird geladen…
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.secondary',
        fontSize: 12,
        border: `1px dashed ${SQUAD_COLORS.gold}`,
        borderRadius: 1,
      }}
    >
      Keine Rollierende-Wertentwicklung-Daten
    </Box>
  );
};

export async function captureSquadRollingChart(): Promise<string | undefined> {
  try {
    const res: any = await ApexCharts.exec(SQUAD_ROLLING_CHART_ID, 'dataURI', { scale: 3 });
    return res?.imgURI;
  } catch {
    return undefined;
  }
}
